import { get, isEmpty, set, isNumber } from 'lodash'
import moment from 'moment'

import { getMatchingProducts } from './general'
import { getMinMaxBollards } from './terminalVisitUtils'

export function checkIfAFieldIsEmpty(fieldToCheck) {
  return fieldToCheck == null || fieldToCheck === '' || fieldToCheck === undefined
}

export function isValidTimestamp(timestamp) {
  const date = moment(timestamp)
  return typeof timestamp === 'number' && date.isValid()
}

export function getErrorForFormField(errors, formFieldPath) {
  const error = get(errors, formFieldPath)

  if (error) {
    return true
  }

  return false
}

export function getErrorMessageForFormField(errors, formFieldPath) {
  const error = get(errors, formFieldPath)

  if (error) {
    return error
  }

  return null
}

export function validateTerminalVisitProperties(terminalVisit) {
  const formErrors = {}

  if (checkIfAFieldIsEmpty(terminalVisit.status)) {
    setErrorIfPresent(formErrors, 'status', 'Required')
  }

  const delayErrors = validateDelays(terminalVisit)
  if (delayErrors) {
    setErrorIfPresent(formErrors, 'delays', delayErrors)
  }

  const demurrageErrors = validateDemurrages(terminalVisit)
  setErrorIfPresent(formErrors, 'demurrages', demurrageErrors)

  const shipErrors = validateTerminalVisitShip(terminalVisit)
  setErrorIfPresent(formErrors, 'ship', shipErrors)

  const operationalStatusErrors = validateOperationalStatus(terminalVisit)
  setErrorIfPresent(formErrors, 'ataTime', operationalStatusErrors.ataTime)
  setErrorIfPresent(formErrors, 'atdTime', operationalStatusErrors.atdTime)

  return formErrors
}

export function validateTerminalVisitOrders(terminalVisit, allProducts, config, isOnExternalQuay) {
  const orderFormErrors = {}
  const { minBollard, maxBollard } = getMinMaxBollards(config.quays)

  terminalVisit.orders.forEach((order, index) => {
    const pathToOrder = `orders[${index}]`

    if (!isOnExternalQuay) {
      // no need to validate bollards on the external quay
      const validatedBollards = validateBollards(order, minBollard, maxBollard)
      setErrorIfPresent(orderFormErrors, pathToOrder.concat('bollardStart'), validatedBollards.bollardStart)
      setErrorIfPresent(orderFormErrors, pathToOrder.concat('bollardEnd'), validatedBollards.bollardEnd)
    }

    const cargoStartEndValidation = validateStartEndDate(`cargoStart`, order.cargoStart, `cargoEnd`, order.cargoEnd)
    setErrorIfPresent(orderFormErrors, pathToOrder.concat('.cargoStart'), cargoStartEndValidation.cargoStart)
    setErrorIfPresent(orderFormErrors, pathToOrder.concat('.cargoEnd'), cargoStartEndValidation.cargoEnd)
    const amountValidation = validateNumberField(order.amount, true, 1)
    setErrorIfPresent(orderFormErrors, pathToOrder.concat('.amount'), amountValidation)

    // don't validate laycan if neither of the fields has been filled in. Otherwise check for valid dates
    if (!checkIfAFieldIsEmpty(order.laycanStart) || !checkIfAFieldIsEmpty(order.laycanEnd)) {
      const laycanValidation = validateStartEndDate(
        `laycanStart`,
        order.laycanStart,
        `laycanEnd`,
        order.laycanEnd,
        false
      )
      setErrorIfPresent(orderFormErrors, pathToOrder.concat('.laycanStart'), laycanValidation.laycanStart)
      setErrorIfPresent(orderFormErrors, pathToOrder.concat('.laycanEnd'), laycanValidation.laycanEnd)
    }

    // only validate the deliveryDate if some values was filled in.
    if (!checkIfAFieldIsEmpty(order.administrativeInfo.deliveryDate)) {
      if (!isValidTimestamp(order.administrativeInfo.deliveryDate)) {
        setErrorIfPresent(orderFormErrors, pathToOrder.concat('.administrativeInfo.deliveryDate'), 'Invalid Date')
      }
    }

    if (!checkIfAFieldIsEmpty(order.administrativeInfo.billOfLadingDate)) {
      if (!isValidTimestamp(order.administrativeInfo.billOfLadingDate)) {
        setErrorIfPresent(orderFormErrors, pathToOrder.concat('.administrativeInfo.billOfLadingDate'), 'Invalid Date')
      }
    }

    if (!checkIfAFieldIsEmpty(order.administrativeInfo.freight)) {
      const freightValidation = validateNumberField(order.administrativeInfo.freight, false, 0.1)
      setErrorIfPresent(orderFormErrors, pathToOrder.concat('administrativeInfo.freight'), freightValidation)
    }

    if (!terminalVisit.boardBoard) {
      const matchingProducts = getMatchingProducts(order.product, order.operation, allProducts)
      if (isEmpty(matchingProducts)) {
        set(orderFormErrors, pathToOrder.concat('.product'), 'No matching product found')
      }

      setErrorIfPresent(orderFormErrors, pathToOrder.concat('.amount'), validateNumberField(order.amount, false, 0))
    }

    if (!checkIfAFieldIsEmpty(order.actualAmount)) {
      setErrorIfPresent(
        orderFormErrors,
        pathToOrder.concat('.actualAmount'),
        validateNumberField(order.actualAmount, false, 0)
      )
    }
  })

  return orderFormErrors
}

export function validateTerminalMaintenance(terminalMaintenance, config) {
  const maintenanceErrors = {}

  const { minBollard, maxBollard } = getMinMaxBollards(config.quays)
  const validatedBollards = validateBollards(terminalMaintenance, minBollard, maxBollard)
  setErrorIfPresent(maintenanceErrors, 'bollardStart', validatedBollards.bollardStart)
  setErrorIfPresent(maintenanceErrors, 'bollardEnd', validatedBollards.bollardEnd)

  const maintenanceStartEndValidation = validateStartEndDate(
    `startTime`,
    terminalMaintenance.startTime,
    `endTime`,
    terminalMaintenance.endTime
  )
  setErrorIfPresent(maintenanceErrors, 'startTime', maintenanceStartEndValidation.startTime)
  setErrorIfPresent(maintenanceErrors, 'endTime', maintenanceStartEndValidation.endTime)

  if (checkIfAFieldIsEmpty(terminalMaintenance.message)) {
    setErrorIfPresent(maintenanceErrors, 'message', 'Required')
  }

  return maintenanceErrors
}

export function validateCargoOperations(cargoOperationsDetails) {
  const cargoOpsErrors = {}

  // don't validate laycan if neither of the fields has been filled in. Otherwise check for valid dates
  if (!isValidTimestamp(cargoOperationsDetails.billOfLoadingDate)) {
    set(cargoOpsErrors, 'billOfLoadingDate', 'Invalid Date')
  }

  if (!isValidTimestamp(cargoOperationsDetails.arrivalTime)) {
    set(cargoOpsErrors, 'arrivalTime', 'Invalid Date')
  }

  if (!isValidTimestamp(cargoOperationsDetails.releaseTimeLab)) {
    set(cargoOpsErrors, 'releaseTimeLab', 'Invalid Date')
  }

  if (!isValidTimestamp(cargoOperationsDetails.dateOfAttestationOfUnloading)) {
    set(cargoOpsErrors, 'dateOfAttestationOfUnloading', 'Invalid Date')
  }

  setErrorIfPresent(
    cargoOpsErrors,
    'billOfLoadingWeight',
    validateNumberField(cargoOperationsDetails.billOfLoadingWeight, false, 0)
  )
  setErrorIfPresent(
    cargoOpsErrors,
    'deliveredSamples',
    validateNumberField(cargoOperationsDetails.deliveredSamples, false, 0)
  )
  setErrorIfPresent(
    cargoOpsErrors,
    'densityDuringLoading',
    validateNumberField(cargoOperationsDetails.densityDuringLoading, false, 0)
  )
  setErrorIfPresent(
    cargoOpsErrors,
    'bargeMeasurementDuringLoading',
    validateNumberField(cargoOperationsDetails.bargeMeasurementDuringLoading, false, 0)
  )

  setErrorIfPresent(
    cargoOpsErrors,
    `temperatureDuringLoading`,
    validateNumberField(cargoOperationsDetails.temperatureDuringLoading, false, -200, 1000)
  )

  setErrorIfPresent(cargoOpsErrors, 'slobTanks', validateSlopTanks(cargoOperationsDetails.slobTanks))
  setErrorIfPresent(cargoOpsErrors, 'measurements', validateMeasurements(cargoOperationsDetails.measurements))
  setErrorIfPresent(
    cargoOpsErrors,
    'unloadingDetails',
    validateUnloadingDetails(cargoOperationsDetails.unloadingDetails)
  )

  return cargoOpsErrors
}

export function validateCargoOperationsYara(cargoOperationDetails) {
  const cargoOpsErrorsYara = {}

  if (!checkIfAFieldIsEmpty(cargoOperationDetails.arrivalTime)) {
    if (!isValidTimestamp(cargoOperationDetails.arrivalTime)) {
      set(cargoOpsErrorsYara, 'arrivalTime', 'Invalid Date')
    }
  }

  const startEndTime = validateStartEndDate(
    `startTime`,
    cargoOperationDetails.startTime,
    `stopTime`,
    cargoOperationDetails.stopTime,
    false
  )
  setErrorIfPresent(cargoOpsErrorsYara, 'startTime', startEndTime.startTime)
  setErrorIfPresent(cargoOpsErrorsYara, 'stopTime', startEndTime.stopTime)
  if (isNumber(cargoOperationDetails.actualAmount)) {
    setErrorIfPresent(
      cargoOpsErrorsYara,
      'actualAmount',
      validateNumberField(cargoOperationDetails.actualAmount, false, 0)
    )
  }

  return cargoOpsErrorsYara
}

function validateBollards(order, minBollard, maxBollard) {
  const bollardErrors = {}
  let bollardsValid = true

  if (checkIfAFieldIsEmpty(order.bollardStart)) {
    bollardsValid = false
    set(bollardErrors, 'bollardStart', 'Required')
  }
  if (checkIfAFieldIsEmpty(order.bollardEnd)) {
    bollardsValid = false
    set(bollardErrors, 'bollardEnd', 'Required')
  }

  const errBollardStart = validateNumberField(order.bollardStart, true, minBollard, maxBollard)
  if (errBollardStart) {
    bollardsValid = false
    set(bollardErrors, 'bollardStart', errBollardStart)
  }

  const errBollardEnd = validateNumberField(order.bollardEnd, true, minBollard, maxBollard)
  if (errBollardEnd) {
    bollardsValid = false
    set(bollardErrors, 'bollardEnd', errBollardEnd)
  }

  if (bollardsValid) {
    if (order.bollardStart >= order.bollardEnd) {
      set(bollardErrors, 'bollardStart', 'Start bollard must be before end bollard')
      set(bollardErrors, 'bollardEnd', 'End bollard must be after start bollard')
    }

    if (order.bollardStart > maxBollard || order.bollardStart < minBollard) {
      set(bollardErrors, 'bollardStart', 'Bollard must be within quay limits')
    }

    if (order.bollardEnd > maxBollard || order.bollardEnd < minBollard) {
      set(bollardErrors, 'bollardEnd', 'Bollard must be within quay limits')
    }
  }

  return bollardErrors
}

function validateDelays(terminalVisit) {
  const delayErrors = []

  if (terminalVisit.delays && terminalVisit.delays.length > 0) {
    terminalVisit.delays.forEach((delay, index) => {
      const validatedStartEndDates = validateStartEndDate(`startTime`, delay.startTime, `endTime`, delay.endTime)
      if (!isEmpty(validatedStartEndDates)) {
        delayErrors[index] = validatedStartEndDates
      }
    })
  }

  return delayErrors
}

function validateDemurrages(terminalVisit) {
  const demurrageErrors = []

  if (terminalVisit.demurrages && terminalVisit.demurrages.length > 0) {
    terminalVisit.demurrages.forEach((demurrage, index) => {
      const demurrageError = {}
      if (demurrage.allowedDuration && demurrage.allowedDuration < 0) {
        set(demurrageError, 'allowedDuration', 'Must be greater than 0')
      }
      if (demurrage.demurrageInCents && demurrage.demurrageInCents < 0) {
        set(demurrageError, 'demurrageInCents', 'Must be greater than 0')
      }

      if (!isEmpty(demurrageError)) {
        demurrageErrors[index] = demurrageError
      }
    })
  }

  return demurrageErrors
}

function validateOperationalStatus(terminalVisit) {
  const operationalStatusErrors = {}
  let datesValid = true
  // validate the ata/etd times based on the status that was set.
  if (terminalVisit.status && (terminalVisit.status === 'moored' || terminalVisit.status === 'sailed')) {
    if (!isValidTimestamp(terminalVisit.ataTime)) {
      datesValid = false
      set(operationalStatusErrors, 'ataTime', 'Invalid Date')
    }
    if (checkIfAFieldIsEmpty(terminalVisit.ataTime)) {
      datesValid = false
      set(operationalStatusErrors, 'ataTime', 'Required')
    }
  }

  if (terminalVisit.status && terminalVisit.status === 'sailed') {
    if (!isValidTimestamp(terminalVisit.atdTime)) {
      datesValid = false
      set(operationalStatusErrors, 'atdTime', 'Invalid Date')
    }
    if (checkIfAFieldIsEmpty(terminalVisit.atdTime)) {
      datesValid = false
      set(operationalStatusErrors, 'atdTime', 'Required')
    }
  }

  if (datesValid && terminalVisit.status === 'sailed') {
    if (terminalVisit.ataTime > terminalVisit.atdTime) {
      set(operationalStatusErrors, 'ataTime', 'Start time must be before the end time')
    }
  }

  return operationalStatusErrors
}

function validateSlopTanks(slopTankValues) {
  const slopTankErrors = {}

  if (slopTankValues) {
    Object.entries(slopTankValues).forEach(([key, value]) => {
      setErrorIfPresent(slopTankErrors, key, validateNumberField(value, false, 0))
    })
  }

  return slopTankErrors
}

function validateMeasurements(measurements) {
  const measurementErrors = []

  if (measurements) {
    measurements.forEach((measurement, index) => {
      const measurementError = {}

      if (checkIfAFieldIsEmpty(measurement.tank)) {
        set(measurementError, `tank`, 'Required')
      }
      setErrorIfPresent(measurementError, `measuredDepthCm`, validateNumberField(measurement.measuredDepthCm, false, 0))
      setErrorIfPresent(measurementError, `volume`, validateNumberField(measurement.volume, false, 0))
      setErrorIfPresent(
        measurementError,
        `temperature`,
        validateNumberField(measurement.temperature, false, -200, 1000)
      )
      if (!isEmpty(measurementError)) {
        measurementErrors[index] = measurementError
      }
    })
  }

  return measurementErrors
}

function validateUnloadingDetails(unloadingDetails) {
  const unloadingDetailErrors = []

  if (unloadingDetails) {
    unloadingDetails.forEach((unloadingDetail, index) => {
      const unloadingError = {}

      const validatedStartEndDates = validateStartEndDate(
        `startTime`,
        unloadingDetail.startTime,
        `endTime`,
        unloadingDetail.endTime
      )
      setErrorIfPresent(unloadingError, 'startTime', validatedStartEndDates.startTime)
      setErrorIfPresent(unloadingError, 'endTime', validatedStartEndDates.endTime)
      setErrorIfPresent(unloadingError, `startLevel`, validateNumberField(unloadingDetail.startLevel, false, 0))
      setErrorIfPresent(unloadingError, `endLevel`, validateNumberField(unloadingDetail.endLevel, false, 0))
      if (!isEmpty(unloadingError)) {
        unloadingDetailErrors[index] = unloadingError
      }
    })
  }

  return unloadingDetailErrors
}

// helper function to validate two dates at the same time, where one date needs to occur before the other.
function validateStartEndDate(startDateLabel, startDateValue, endDateLabel, endDateValue, required = true) {
  const startEndDateErrors = {}

  // Check if start date is filled in, and show error only when empty and it's required
  const startIsEmpty = !startDateValue
  if (required && startIsEmpty) {
    set(startEndDateErrors, `${startDateLabel}`, 'Start date is required')
  }

  // When start date is filled in, check if it's valid
  if (startDateValue && !isValidTimestamp(startDateValue)) {
    set(startEndDateErrors, `${startDateLabel}`, 'Invalid Date')
  }

  // Check if end date is filled in, and show error only when empty and it's required
  const endIsEmpty = !endDateValue
  if (required && endIsEmpty) {
    set(startEndDateErrors, `${endDateLabel}`, 'End date is required')
  }

  // When end date is filled in, check if it's valid
  if (endDateValue && !isValidTimestamp(endDateValue)) {
    set(startEndDateErrors, `${endDateLabel}`, 'Invalid Date')
  }

  // then check the order of the dates. Check if the startDate is before the endDate, otherwise set error.
  if (endDateValue && isValidTimestamp(endDateValue) && isEmpty(startEndDateErrors)) {
    if (startDateValue >= endDateValue) {
      set(startEndDateErrors, `${startDateLabel}`, 'Start time must be before end time')
    }
  }

  return startEndDateErrors
}

// function validateOrderEndAndAtd(endDateValue, visit) {
//   const orderAtdErrors = {}

//   if (endDateValue > visit.atdTime) {
//     set(orderAtdErrors, 'cargoEnd', "Cargo end time can't be after ATD")
//   }

//   return orderAtdErrors
// }

function validateTerminalVisitShip(terminalVisit) {
  let shipErrors = {}

  if (terminalVisit.ship) {
    if (checkIfAFieldIsEmpty(terminalVisit.ship.length)) {
      set(shipErrors, 'length', 'Required')
    } else {
      if (terminalVisit.ship.length <= 0) {
        set(shipErrors, 'length', 'Length must be greater than 0')
      }
    }

    if (checkIfAFieldIsEmpty(terminalVisit.ship.type)) {
      set(shipErrors, 'type', 'Required')
    }
  } else {
    shipErrors = {
      length: 'Required',
      type: 'Required'
    }
  }

  return shipErrors
}

/**
 * Sets the error value in the errorObject if it is defined, not an empty object and not and empty array
 * @param {*} errorObject
 * @param {*} path
 * @param {*} value
 */
function setErrorIfPresent(errorObject, path, value) {
  if (value && !isEmpty(value)) {
    set(errorObject, path, value)
  }
}

/**
 * Validates a number field. Checks this based on if the value is required, a min value and a max value.
 */
export function validateNumberField(value, required, min, max) {
  if (checkIfAFieldIsEmpty(value) && required === true) {
    return 'Required'
  }

  if (required && isNaN(value)) {
    return 'Please fill in a number'
  }

  if (value && !required && value !== '' && isNaN(value)) {
    return 'Value must be a number'
  }

  if (min !== undefined) {
    if (value < min) {
      return `Value must be greater than ${min}`
    }
  }

  if (max !== undefined) {
    if (value > max) {
      return `Value must be lower than ${max}`
    }
  }

  return undefined
}

/**
 * Updates the visits with the current changes in the formfield
 * @param {TerminalVisit[]} terminalVisits
 * @param {TerminalVisit} formFields
 * @return {TerminalVisit[]}
 */
export function updateVisitsWithFormFields(terminalVisits, formFields) {
  const updatedVisitExistInVisits = terminalVisits.find(visit => visit._id === formFields._id)

  return updatedVisitExistInVisits
    ? [...terminalVisits.filter(visit => visit._id !== formFields._id), formFields]
    : [...terminalVisits, formFields]
}
